import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { StatusPill } from "./AdminDashboard";
import { EmailCustomerDialog, downloadCSV } from "../../lib/admin-helpers";
import { Mail, Download } from "lucide-react";
import { AdminPhoneActions } from "../../components/AdminPhoneActions";
import { formatPhoneForDisplay } from "../../lib/phone";
import { formatISK } from "../../lib/currency";
import { PaydayEnvBadge } from "../../components/PaydayEnvBadge";

const STATUSES = ["pending_payment", "confirmed_uninvoiced", "confirmed_payment_pending", "pending", "confirmed", "cancelled", "completed"];

export default function AdminBookings() {
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [quoteFilter, setQuoteFilter] = useState("all");
  const [emailTarget, setEmailTarget] = useState(null);
  const [paydayConfig, setPaydayConfig] = useState(null);
  const [actioning, setActioning] = useState("");

  const load = async () => {
    const [b, v, d, p] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/vehicles"), api.get("/admin/drivers"), api.get("/admin/payday/config")]);
    setRows(b.data);
    setVehicles(v.data);
    setDrivers(d.data || []);
    setPaydayConfig(p.data);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  useEffect(() => {
    if (location.state?.createdBookingId) {
      toast.success(`Booking created · ${location.state.createdBookingId}`);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const update = async (id, patch) => {
    try {
      await api.put(`/admin/bookings/${id}`, patch);
      toast.success("Booking updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const runBookingAction = async (id, action, request) => {
    setActioning(`${id}:${action}`);
    try {
      await request();
      toast.success(action);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setActioning("");
    }
  };

  const filtered = rows.filter((r) => {
    const statusMatch = filter === "all" ? true : r.status === filter;
    const quoteRequired = r.quote_required || r.pricing_mode === "quote_required";
    const quoteMatch = quoteFilter === "all"
      ? true
      : quoteFilter === "needs_quote"
      ? quoteRequired
      : !quoteRequired;
    return statusMatch && quoteMatch;
  });
  const quoteCount = rows.filter((r) => r.quote_required || r.pricing_mode === "quote_required").length;
  const vname = (id) => vehicles.find((v) => v.id === id)?.name || "—";
  const dname = (id) => drivers.find((d) => d.id === id)?.name || "—";

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Bookings</h1>
          <p className="text-sm text-slate-500">Confirm, reassign or cancel requests.</p>
          <PaydayEnvBadge config={paydayConfig} testId="admin-bookings-payday-badge" />
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/bookings/new"
            data-testid="create-booking-button"
            className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            + Create Booking
          </Link>
          <button
            onClick={() => downloadCSV("/admin/bookings.csv", "bookings.csv")}
            data-testid="export-bookings-csv"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Download size={14} /> Export CSV
          </button>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="booking-filter-status"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={quoteFilter}
            onChange={(e) => setQuoteFilter(e.target.value)}
            data-testid="booking-filter-needs-quote"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="all">All pricing</option>
            <option value="needs_quote">Needs quote</option>
            <option value="fixed_only">Fixed price only</option>
          </select>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="booking-filter-summary">
        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900 ring-1 ring-inset ring-amber-200">
          {quoteCount} quote-required booking{quoteCount === 1 ? "" : "s"}
        </span>
        {quoteFilter === "needs_quote" && (
          <span className="text-xs font-medium text-slate-500">Showing only manual-pricing bookings</span>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Dates</th>
                <th className="px-4 py-3">Vehicle</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">No bookings</td></tr>
              )}
              {filtered.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/admin/bookings/${b.id}`} data-testid={`open-booking-detail-${b.id}`} className="font-medium text-slate-900 hover:underline">{b.customer_name}</Link>
                    <p className="text-xs text-slate-500">{b.email}{b.phone ? ` · ${formatPhoneForDisplay(b.phone)}` : ""}</p>
                    {b.preferred_contact_method && (
                      <p className="mt-1 text-[11px] text-slate-400 capitalize">Prefers {b.preferred_contact_method}</p>
                    )}
                    <AdminPhoneActions
                      phone={b.phone}
                      preferredContactMethod={b.preferred_contact_method}
                      testIdPrefix={`booking-phone-${b.id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="capitalize">{b.service_type}</span>
                    {b.transport_service_kind && (
                      <p className="mt-1 text-[11px] text-slate-500 capitalize">
                        {b.transport_service_kind.replace(/_/g, " ")}
                        {b.pricing_tier ? ` · ${b.pricing_tier.toUpperCase()}` : ""}
                      </p>
                    )}
                    {b.tour_name && <p className="text-xs text-slate-500">{b.tour_name}</p>}
                    {(b.pickup_location_name_snapshot || b.pickup) && (
                      <p className="text-xs text-slate-500">
                        {b.pickup_is_custom_address ? "Other" : (b.pickup_location_name_snapshot || b.pickup)}
                        {" → "}
                        {b.dropoff_is_custom_address ? "Other" : (b.dropoff_location_name_snapshot || b.dropoff)}
                      </p>
                    )}
                    {b.pickup_is_custom_address && b.pickup_custom_address && (
                      <p className="text-[11px] text-slate-400">Pickup: {b.pickup_custom_address}</p>
                    )}
                    {b.dropoff_is_custom_address && b.dropoff_custom_address && (
                      <p className="text-[11px] text-slate-400">Drop-off: {b.dropoff_custom_address}</p>
                    )}
                    {b.pricing_mode === "fixed_price" && b.fixed_price_isk ? (
                      <p className="text-[11px] mt-0.5">
                        <span className="inline-flex items-center rounded-full bg-sky-100 text-sky-900 ring-1 ring-inset ring-sky-200 px-2 py-0.5 font-semibold">
                          {formatISK(b.fixed_price_isk)} · fixed
                        </span>
                      </p>
                    ) : b.quote_required ? (
                      <p className="text-[11px] mt-0.5">
                        <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-200 px-2 py-0.5 font-semibold">
                          Quote required
                        </span>
                      </p>
                    ) : null}
                    {(b.service_type === "transport" || b.service_type === "tours") && (
                      <p className="text-[11px] mt-1">
                        {b.driver_id ? (
                          <span className="text-slate-500">Driver: <span className="font-semibold text-slate-700">{dname(b.driver_id)}</span></span>
                        ) : (
                          <span data-testid={`booking-driver-unassigned-${b.id}`} className="inline-flex items-center rounded-full bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-200 px-2 py-0.5 font-semibold">
                            Driver unassigned
                          </span>
                        )}
                      </p>
                    )}
                    {(b.invoice_mode || b.vat_rate || b.payday_invoice_status || b.payday_invoice_id || b.invoice_error) && (
                      <div className="mt-2 space-y-1 text-[11px] text-slate-500" data-testid={`booking-invoice-meta-${b.id}`}>
                        {b.invoice_mode && <p>Mode: <span className="font-semibold text-slate-700">{b.invoice_mode}</span></p>}
                        {b.payment_method && <p>Payment method: <span className="font-semibold text-slate-700">{b.payment_method}</span></p>}
                        {b.payment_status && <p>Payment status: <span className="font-semibold text-slate-700">{b.payment_status}</span></p>}
                        {b.vat_rate != null && <p>VAT: <span className="font-semibold text-slate-700">{b.vat_rate}%</span></p>}
                        {b.payday_invoice_status && <p>Payday status: <span className="font-semibold text-slate-700">{b.payday_invoice_status}</span></p>}
                        {b.payday_invoice_number && <p>Invoice no: <span className="font-semibold text-slate-700">{b.payday_invoice_number}</span></p>}
                        {b.payday_invoice_id && <p className="break-all">Invoice ID: <span className="font-semibold text-slate-700">{b.payday_invoice_id}</span></p>}
                        {b.payday_last_synced_at && <p>Last sync: <span className="font-semibold text-slate-700">{new Date(b.payday_last_synced_at).toLocaleString()}</span></p>}
                        {b.invoice_error && <p className="text-red-600">Error: {b.invoice_error}</p>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <p>{new Date(b.start_at).toLocaleString()}</p>
                    <p className="text-xs text-slate-500">→ {new Date(b.end_at).toLocaleString()}</p>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={b.vehicle_id || ""}
                      onChange={(e) => update(b.id, { vehicle_id: e.target.value || null })}
                      data-testid={`assign-vehicle-${b.id}`}
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                    >
                      <option value="">Unassigned</option>
                      {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                    {!b.vehicle_id && <p className="text-xs text-slate-400 mt-1">{vname(b.vehicle_id)}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={b.status}
                      onChange={(e) => update(b.id, { status: e.target.value })}
                      data-testid={`status-${b.id}`}
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <div className="mt-1 flex items-center gap-1.5">
                      <StatusPill status={b.status} />
                      {b.deposit_status === "paid" && (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-900 ring-1 ring-inset ring-emerald-200 px-2 py-0.5 text-[10px] font-semibold">Deposit paid</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEmailTarget(b)}
                      data-testid={`email-booking-${b.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900 mr-3"
                    >
                      <Mail size={12} /> Email
                    </button>
                    {!b.payday_invoice_id && (
                      <button
                        onClick={() => runBookingAction(b.id, "Payday draft created", () => api.post(`/admin/bookings/${b.id}/invoice/create-draft`))}
                        disabled={actioning === `${b.id}:Payday draft created`}
                        data-testid={`create-payday-draft-${b.id}`}
                        className="mr-3 text-xs font-semibold text-sky-700 hover:text-sky-900 disabled:opacity-50"
                      >
                        Create draft
                      </button>
                    )}
                    {b.payday_invoice_id && (
                      <>
                        <button
                          onClick={() => runBookingAction(b.id, "Payday invoice synced", () => api.post(`/admin/bookings/${b.id}/invoice/sync`))}
                          disabled={actioning === `${b.id}:Payday invoice synced`}
                          data-testid={`sync-payday-invoice-${b.id}`}
                          className="mr-3 text-xs font-semibold text-slate-700 hover:text-slate-900 disabled:opacity-50"
                        >
                          Sync invoice
                        </button>
                      </>
                    )}
                    {b.invoice_mode === "cash_manual" && b.payment_status !== "paid" && (
                      <button
                        onClick={() => runBookingAction(b.id, "Payment marked received", () => api.post(`/admin/bookings/${b.id}/payment-received`))}
                        disabled={actioning === `${b.id}:Payment marked received`}
                        data-testid={`mark-payment-received-${b.id}`}
                        className="mr-3 text-xs font-semibold text-amber-700 hover:text-amber-900 disabled:opacity-50"
                      >
                        Mark payment received
                      </button>
                    )}
                    <button
                      onClick={() => remove(b.id)}
                      data-testid={`delete-booking-${b.id}`}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {emailTarget && <EmailCustomerDialog booking={emailTarget} onClose={() => setEmailTarget(null)} />}
    </div>
  );
}
